import type { RoomOccupancy, RoomsParticipantDistribution } from '@/domain/shared/types'
import type { Parser, QueryStringParameters } from '../../../common/infrastructure/query-strings/queryStringSerializer'
import { CHILDREN_MAX_AGE, CHILDREN_MIN_AGE } from '@/domain/shared/constants'
import { createRoomsParticipantDistributionMapper } from '@/scopes/search/search/infrastructure/api/filtersSelectionMapper/mappers/roomsParticipantDistributionMapper'
import { format, sub } from 'date-fns'
import { isEmpty } from 'ramda'

export interface Filters {
  DepartureAirport?: string[]
  Duration?: string[]
  Mealplan?: string
  Month?: string
  TransportType?: string
  Participants?: RoomOccupancy[]
}

export interface StateSelection {
  FocusPriceTable?: boolean
  SelectedPriceId?: string
  Room?: string
  RoomContractId?: string[]
  SelectedOutboundRouteId?: string
  SelectedInboundRouteId?: string
}

export interface ParsedInitialState {
  DepartureAirport?: string[]
  Duration?: string[]
  RoomType?: string[]
  Participants?: RoomOccupancy[]
  Month?: string
  Mealplan?: string
  TransportType?: string
  DepartureDate?: string[]
  StateSelection?: StateSelection
}

interface Dependencies {
  roomsParticipantDistributionMapper: ReturnType<typeof createRoomsParticipantDistributionMapper>
}

const defaultParticipants: RoomOccupancy[] = [
  {
    adults: 2,
    children: [],
    babies: [],
  },
]

function lowercaseKeys(obj: Record<string, any>): Record<string, any> {
  return Object.keys(obj).reduce((acc, key) => {
    acc[key.toLowerCase()] = obj[key]
    return acc
  }, {} as Record<string, any>)
}

export function createPricesFiltersParserWithInitState(dependencies: Partial<Dependencies> = {}): Parser<Filters> {
  const { roomsParticipantDistributionMapper = createRoomsParticipantDistributionMapper() } = dependencies

  function serialize({ Participants, ...rest }: ParsedInitialState) {
    return {
      ...rest,
      Participants:
        Participants && !isEmpty(Participants)
          ? roomsParticipantDistributionMapper(Participants ?? [])
          : roomsParticipantDistributionMapper(defaultParticipants),
    }
  }

  function parse(input: QueryStringParameters): ParsedInitialState {
    const inputLowercased = lowercaseKeys(input)
    const derivedMonth = getMonth(inputLowercased.month as string, inputLowercased.departuredate as string[])
    return {
      DepartureAirport: (inputLowercased.departureairport as string[]) ?? [],
      Duration: (inputLowercased.duration as string[]) ?? [],
      RoomType: (inputLowercased.roomtype as string[]) ?? [],
      Participants: inputLowercased.participants ? mapParticipants(inputLowercased.participants as string[][]) : defaultParticipants,
      ...(derivedMonth && { Month: derivedMonth }),
      ...(inputLowercased.mealplan ? { Mealplan: getFilterValue(inputLowercased.mealplan as string | string[]) } : {}),
      ...(inputLowercased.transporttype ? { TransportType: getFilterValue(inputLowercased.transporttype as string | string[]) } : {}),
      ...((inputLowercased.departuredate as string[]) && { DepartureDate: inputLowercased.departuredate as string[] }),
      StateSelection: {
        ...((inputLowercased.focuspricetable as string) && { FocusPriceTable: Boolean(inputLowercased.focuspricetable) }),
        ...((inputLowercased.selectedpriceid as string) && { SelectedPriceId: inputLowercased.selectedpriceid as string }),
        ...((inputLowercased.room as string) && { Room: inputLowercased.room as string }),
        ...((inputLowercased.roomcontractid as string[]) && { RoomContractId: inputLowercased.roomcontractid as string[] }),
        ...((inputLowercased.selectedoutboundrouteid as string) && { SelectedOutboundRouteId: inputLowercased.selectedoutboundrouteid as string }),
        ...((inputLowercased.selectedinboundrouteid as string) && { SelectedInboundRouteId: inputLowercased.selectedinboundrouteid as string }),
      },
    }
  }

  function mapParticipants(rooms: string[][]) {
    const adultAgeThreshold = sub(new Date(), { years: CHILDREN_MAX_AGE, days: 1 })
    const childrenAgeThreshold = sub(new Date(), { years: CHILDREN_MIN_AGE, days: 1 })

    const mappedParticipants: RoomsParticipantDistribution = []

    rooms.forEach((participants) => {
      const result: RoomOccupancy = {
        adults: 0,
        children: [],
        babies: [],
      }

      participants.forEach((participantBirthDate) => {
        const birthDate = new Date(participantBirthDate)

        if (birthDate < adultAgeThreshold) {
          result.adults += 1
        }
        else if (birthDate < childrenAgeThreshold) {
          result.children.push(birthDate)
        }
        else {
          result.babies.push(birthDate)
        }
      })

      mappedParticipants.push(result)
    })

    return mappedParticipants
  }

  function getMonth(month?: string, date?: string[]) {
    if (month) {
      return month
    }
    if (date && date.length > 0) {
      return format(new Date(date[0]), 'yyyy-MM')
    }
    return null
  }

  function getFilterValue(value: string | string[]): string {
    return value && Array.isArray(value) ? value.join() : (value as string)
  }
  return { serialize, parse }
}
